import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { distinctUntilChanged, takeWhile } from 'rxjs/operators';
import { LoadingSpinnerService } from '../../../core/data/loading-spinner.service';
import { IBoard } from '../../../core/models/board.model';
import { ICurrentBoard } from '../../../core/models/current-board.model';
import { defaultDisplayAttributes } from '../../../core/models/default-display-attributes.const';
import { IFilterData } from '../../../core/models/filter-data.model';
import { IPaginationLabels } from '../../../core/models/pagination-labels.model';
import { IProductAttributeParameters } from '../../../core/models/product-attribute-parameters.model';
import { IProductAttributeRow } from '../../../core/models/product-attribute-row.model';
import { IProductDisplayAttribute } from '../../../core/models/product-display-attribute.model';
import { IState } from '../../../core/store/store.model';
import { selectBoardData, selectFilterData } from '../../../core/store/store.selectors';
import { AttributeService } from '../../../shared/services/attribute.service';
import { DefaultFiltersService } from '../../../shared/services/default-filters.service';
import { ListingDisplayMode } from '../../../core/models/display-mode.enum';

@Component({
    selector: 'lynkd-pattern-attribute-listing',
    templateUrl: './attribute-listing.component.html',
    styleUrls: ['./attribute-listing.component.scss']
})
export class AttributeListingComponent implements OnInit, OnDestroy {
    @Input() public gallery: ListingDisplayMode;
    public attributeParameters: IProductAttributeParameters;
    public currentBoard: ICurrentBoard = {
        user_id: '',
        board_id: 0,
        board_name: '',
        time_ranges: undefined as string,
        time_periods: [],
        time_years: [],
        time_quarters: [],
        time_months: [],
        time_weeks: [],
        location_companies: [],
        location_divisions: [],
        location_areas: [],
        location_regions: [],
        location_stores: [],
        fields: '',
        departments: [],
        sub_departments: [],
        categories: [],
        product_types: [],
        meta_datas: {},
        metric_ranges: [],
        display_metrics: [],
        type: '',
        exclusions: [],
        display_attributes: defaultDisplayAttributes,
        display_attribute_metrics: []
    };
    public attributes: Array<IProductDisplayAttribute>;
    public itemsPerPage: number = 3;
    public attributeRows: Array<{ attribute: IProductDisplayAttribute; rows: Array<IProductAttributeRow> }> = [];
    public currentPage: number;
    public labels: IPaginationLabels = {
        previousLabel: '',
        nextLabel: ''
    };
    private _isActive: boolean = true;
    /**
     * Increments on every filterData emission. The subscribe handler is async and writes to
     * instance state after awaiting, so two emissions can interleave and an older, slower one can
     * finish last and overwrite a newer result. Each run captures the token and abandons its
     * writes once a newer emission has started.
     */
    private _filterDataSequence: number = 0;

    public constructor(private readonly _store: Store<IState>,
                       private readonly _defaultFilters: DefaultFiltersService,
                       private readonly _attributeService: AttributeService,
                       private readonly _spinnerService: LoadingSpinnerService) {
    }

    public async ngOnInit(): Promise<void> {
        this.attributes = await this._attributeService.getProductDisplayAttributes();

        // Put the analytics defaults into the store before subscribing, so the first emission
        // already carries display_attributes. Landing here via a refresh constructs this component
        // while that request is still in flight, so otherwise the first emission is the initial
        // store state -- which has no display_attributes at all -- and falls back to the single
        // hardcoded default. Navigating in from another tab never hit this because the dashboard
        // had already hydrated the store by then.
        // Must be hydrate, not get: getAnalyticsDefaults only fetches and caches, it does not
        // reach the store. The fetch is cached and the dispatch is idempotent, so calling this
        // here costs no extra request even though the dashboard also calls it.
        try {
            await this._defaultFilters.hydrateAnalyticsDefaults();
        } catch (e) {
            // Non-fatal: the emission below still applies whatever the store holds.
        }

        this._store.select(selectBoardData)
            .pipe(takeWhile(() => this._isActive))
            .subscribe((result: IBoard) => {
                this.currentBoard.board_id = result.board_id;
                this.currentBoard.board_name = result.board_name;
                this.currentBoard.type = result.type;
            });

        this._store
            // .select(selectFilterData)
            // .pipe(
            //     takeWhile(() => this._isActive)
            // )
            .pipe(
                select(selectFilterData),
                distinctUntilChanged((previous?: IFilterData, current?: IFilterData) => JSON.stringify(previous) === JSON.stringify(current)),
                takeWhile(() => this._isActive)
            )
            .subscribe(async (result: IFilterData) => {
                // Claim this run. Anything that arrives later supersedes it, so every write below
                // is skipped once the token has moved on -- otherwise a slower earlier emission
                // finishes last and overwrites the newer attributes and rows.
                const sequence: number = ++this._filterDataSequence;
                const isCurrent: () => boolean = () => sequence === this._filterDataSequence;

                this._spinnerService.spin$.next(true);

                try {
                    const oldAttributeParameters: IProductAttributeParameters = this.attributeParameters;
                    const attributeParameters: IProductAttributeParameters = await this.setAttributeParameters(result);

                    if (!isCurrent()) {
                        return;
                    }

                    this.attributeParameters = attributeParameters;
                    this.currentPage = 1;

                    const attributes: Array<IProductDisplayAttribute> = await this.hydrateAttributes();

                    if (!isCurrent()) {
                        return;
                    }

                    this.attributes = attributes;

                    if (this.haveParametersChanged(oldAttributeParameters) && this.gallery === ListingDisplayMode.Attributes) {
                        await this._attributeService.getDefaultProductDisplayAttributeMetrics();

                        if (!isCurrent()) {
                            return;
                        }

                        await this.loadAttributeRows(isCurrent);
                    }
                } finally {
                    // Only the newest run owns the spinner; a superseded one must not clear it
                    // while the run that replaced it is still loading.
                    if (isCurrent()) {
                        this._spinnerService.spin$.next(false);
                    }
                }
            });
    }

    public ngOnDestroy(): void {
        this._isActive = false;
    }

    public async changePage($event: number): Promise<void> {
        this.currentPage = $event;
        this._spinnerService.spin$.next(true);
        try {
            await this.loadAttributeRows();
        } catch (e) {
            this._spinnerService.spin$.next(false);
        }
        this._spinnerService.spin$.next(false);
    }

    /**
     * @param isCurrent guards against a superseded run publishing its rows. Each attribute is
     * fetched in sequence, so a run that is overtaken mid-loop must stop rather than finish last
     * and overwrite the newer result.
     */
    private async loadAttributeRows(isCurrent: () => boolean = (): boolean => true): Promise<void> {
        // console.log("in LoadAttributeRows, making call to getProductAttributes with gallery: ", this.gallery);

        this.attributeRows = [];
        const skip: number = (this.currentPage - 1)
            * this.itemsPerPage;
        const take: number = skip + this.itemsPerPage;
        const attributesOnScreen: Array<IProductDisplayAttribute> = this.attributes.slice(skip, take);
        // Collected locally and published in one assignment, so a superseded run never leaves a
        // partially built table on screen.
        const loadedRows: Array<{ attribute: IProductDisplayAttribute; rows: Array<IProductAttributeRow> }> = [];

        for (const attribute of attributesOnScreen) {
            const rows: Array<IProductAttributeRow> = await this._attributeService
                .getProductAttributes({
                    ...this.attributeParameters,
                    attribute_column: attribute.column_name,
                    attribute_type: attribute.attribute_type
                });

            if (!isCurrent()) {
                return;
            }

            loadedRows.push({
                attribute,
                rows
            });
        }

        this.attributeRows = loadedRows;
    }

    /**
     * Returns the attributes rather than assigning them, so the caller can discard the result if a
     * newer emission has superseded this run.
     */
    private async hydrateAttributes(): Promise<Array<IProductDisplayAttribute>> {
        // If there are default attributes defined in the default filters config table, then apply those default attributes
        // The attributeParameters variable contains the config retrieved from the default filters config table
        if (this.attributeParameters.display_attributes && this.attributeParameters.display_attributes.length > 0) {
            const fullAttributes: Array<IProductDisplayAttribute> = await this._attributeService
                .getProductDisplayAttributes();

            // 1) Only include display_attributes that exist in fullAttributes
            // 2) Retain the exact ordering defined in display_attributes
            // 3) Filter out any undefined values
            return this.attributeParameters.display_attributes
                                .map((d: IProductDisplayAttribute) =>
                                    fullAttributes.find(
                                        (f: IProductDisplayAttribute) => f.column_name === d.column_name
                                    )
                                )
                                .filter((x: IProductDisplayAttribute) => Boolean(x));
        }

        return this._attributeService.getProductDisplayAttributes();
    }

    private async setAttributeParameters(result: IFilterData): Promise<IProductAttributeParameters> {
        
        
        const filterData: IFilterData = JSON.parse(JSON.stringify(result));
        filterData.time_period = await this.calculateTimePeriodFilter(filterData);
        filterData.time_period =
            filterData.time_period.length ||
            filterData.time_year.length ||
            filterData.time_quarter.length ||
            filterData.time_month.length ||
            filterData.time_week.length
                ? filterData.time_period
                : '';

        const attributeParameters: IProductAttributeParameters = {
            attribute_column: '',
            pageSize: 8000,
            ...filterData
        } as IProductAttributeParameters;
        attributeParameters.display_attributes =
            !attributeParameters.display_attributes || attributeParameters.display_attributes.length === 0
                ? defaultDisplayAttributes
                : attributeParameters.display_attributes;
        return attributeParameters;
    }

    private haveParametersChanged(oldAttributeParameters: IProductAttributeParameters): boolean {
        return !oldAttributeParameters ||
            !(
                oldAttributeParameters.attribute_column === this.attributeParameters.attribute_column &&
                oldAttributeParameters.ids === this.attributeParameters.ids &&
                oldAttributeParameters.department === this.attributeParameters.department &&
                oldAttributeParameters.sub_department === this.attributeParameters.sub_department &&
                oldAttributeParameters.category === this.attributeParameters.category &&
                oldAttributeParameters.product_type === this.attributeParameters.product_type &&
                oldAttributeParameters.metric_range === this.attributeParameters.metric_range &&
                oldAttributeParameters.fieldsSet === this.attributeParameters.fieldsSet &&
                oldAttributeParameters.sortOrder === this.attributeParameters.sortOrder &&
                oldAttributeParameters.pageSize === this.attributeParameters.pageSize &&
                oldAttributeParameters.meta_data === this.attributeParameters.meta_data &&
                oldAttributeParameters.location_store === this.attributeParameters.location_store &&
                oldAttributeParameters.location_region === this.attributeParameters.location_region &&
                oldAttributeParameters.location_area === this.attributeParameters.location_area &&
                oldAttributeParameters.location_division === this.attributeParameters.location_division &&
                oldAttributeParameters.location_company === this.attributeParameters.location_company &&
                oldAttributeParameters.time_week === this.attributeParameters.time_week &&
                oldAttributeParameters.time_month === this.attributeParameters.time_month &&
                oldAttributeParameters.time_quarter === this.attributeParameters.time_quarter &&
                oldAttributeParameters.time_year === this.attributeParameters.time_year &&
                oldAttributeParameters.time_range === this.attributeParameters.time_range &&
                oldAttributeParameters.time_period === this.attributeParameters.time_period &&
                oldAttributeParameters.display_attributes === this.attributeParameters.display_attributes
            );
    }

    private hasTimeFilter(filterData: IFilterData): boolean {
        return (
            (filterData.time_week && filterData.time_week.length !== 0) ||
            (filterData.time_month && filterData.time_month.length !== 0) ||
            (filterData.time_quarter && filterData.time_quarter.length !== 0) ||
            (filterData.time_year && filterData.time_year.length !== 0) ||
            (filterData.time_range && filterData.time_range.length !== 0) ||
            (filterData.time_period && filterData.time_period.length !== 0)
        );
    }

    private async calculateTimePeriodFilter(filterData: IFilterData): Promise<string> {
        if (this.hasTimeFilter(filterData)) {
            return filterData.time_period;
        } else {
            const defaults: IFilterData = await this._defaultFilters.getAnalyticsDefaults();
            return defaults?.time_period;
        }
    }
}
